import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FaArrowLeft, FaTh, FaList } from 'react-icons/fa';
import Breadcrumbs from './Breadcrumbs';
import FolderGrid from './FolderGrid';
import FolderList from './FolderList';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';
const VIEW_MODE_KEY = 'folderBrowserViewMode';

const getStoredViewMode = () => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

const FolderBrowser = ({ 
  initialFolderId, 
  onFolderChange, 
  showActions, 
  onEditFolder, 
  onDeleteFolder,
  rightActions,
  filterClass = '',
  filterSubject = '',
  filterCourseTarget = '',
  refreshKey = 0
}) => {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [viewMode, setViewMode] = useState(getStoredViewMode);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // Ignore storage errors; the view still switches for this session
    }
  };

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-menu-container')) {
        setActiveActionMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync folder structure with initialFolderId, filters or refreshKey
  useEffect(() => {
    const targetId = initialFolderId || null;
    const currentId = currentFolder ? currentFolder.id : null;
    
    if (targetId !== currentId) {
      if (targetId) {
        loadFolderById(targetId);
      } else {
        fetchRootFolders();
      }
    } else if (!targetId) {
      // Re-fetch root folders if filter or refreshKey changes at root level
      fetchRootFolders();
    } else {
      // Re-fetch folder contents if filter or refreshKey changes at nested level
      fetchFolderContents(targetId, currentFolder.name);
    }
  }, [initialFolderId, filterClass, filterSubject, filterCourseTarget, refreshKey]);

  // Notify parent on change
  useEffect(() => {
    if (onFolderChange) {
      onFolderChange(currentFolder ? currentFolder.id : null);
    }
  }, [currentFolder]);

  // Load a folder by ID
  const loadFolderById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const folderResponse = await axios.get(`${API_BASE_URL}/Folders/${id}`);
      const folder = folderResponse.data;
      
      if (folder) {
        await fetchFolderContents(id, folder.name);
      } else {
        setError('Folder not found.');
        fetchRootFolders();
      }
    } catch (err) {
      setError('Failed to load folder.');
      console.error('Error loading folder by ID:', err);
      fetchRootFolders();
    } finally {
      setLoading(false);
    }
  };

  // Fetch root folders
  const fetchRootFolders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_BASE_URL}/Folders/root/all`;
      const qParams = new URLSearchParams();
      if (filterClass) qParams.append('class', filterClass);
      if (filterSubject) qParams.append('subject', filterSubject);
      if (filterCourseTarget) qParams.append('course_target', filterCourseTarget);
      
      const qStr = qParams.toString();
      if (qStr) {
        url += `?${qStr}`;
      }
      
      const response = await axios.get(url);
      setFolders(response.data.data || []);
      setCurrentFolder(null);
      setBreadcrumbs([]);
    } catch (err) {
      setError('Failed to load folders.');
      console.error('Error fetching root folders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch contents of a selected folder
  const fetchFolderContents = async (id, folderName) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_BASE_URL}/Folders/parent/${id}`;
      const qParams = new URLSearchParams();
      if (filterClass) qParams.append('class', filterClass);
      if (filterSubject) qParams.append('subject', filterSubject);
      if (filterCourseTarget) qParams.append('course_target', filterCourseTarget);
      
      const qStr = qParams.toString();
      if (qStr) {
        url += `?${qStr}`;
      }
      
      const foldersResponse = await axios.get(url);
      setFolders(foldersResponse.data.data || []);
      
      setCurrentFolder({ id: parseInt(id), name: folderName });
      
      // Update breadcrumbs array
      const index = breadcrumbs.findIndex(crumb => crumb.id === parseInt(id));
      if (index === -1) {
        setBreadcrumbs([...breadcrumbs, { id: parseInt(id), name: folderName }]);
      } else {
        setBreadcrumbs(breadcrumbs.slice(0, index + 1));
      }
    } catch (err) {
      setError('Failed to load folder contents.');
      console.error('Error fetching folder contents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Click handler for subfolders
  const handleFolderClick = (folder) => {
    fetchFolderContents(folder.id, folder.name);
  };

  // Click handler for breadcrumbs
  const handleNavigateBreadcrumb = (id, name, index) => {
    const trimmedBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(trimmedBreadcrumbs);
    fetchFolderContents(id, name);
  };

  // Back button navigation
  const handleNavigateUp = () => {
    if (breadcrumbs.length <= 1) {
      fetchRootFolders();
    } else {
      const parentCrumb = breadcrumbs[breadcrumbs.length - 2];
      const trimmedBreadcrumbs = breadcrumbs.slice(0, breadcrumbs.length - 1);
      setBreadcrumbs(trimmedBreadcrumbs);
      fetchFolderContents(parentCrumb.id, parentCrumb.name);
    }
  };

  const styles = {
    actionsBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    breadcrumbNav: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0.5rem',
      flex: '1 1 auto'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '1rem',
      color: '#374151',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      transition: 'background-color 0.2s',
      fontSize: '0.875rem',
      fontWeight: '500'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '2rem 0'
    },
    spinner: {
      display: 'inline-block',
      width: '2rem',
      height: '2rem',
      borderRadius: '50%',
      border: '4px solid #E5E7EB',
      borderTopColor: '#4423CC', // Fallback color from stylesheet context
      animation: 'spin 1s linear infinite'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem'
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#374151',
      margin: 0
    },
    viewToggle: {
      display: 'inline-flex',
      border: '1px solid #E5E7EB',
      borderRadius: '0.375rem',
      overflow: 'hidden',
      flexShrink: 0
    },
    viewToggleButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.375rem 0.75rem',
      border: 'none',
      background: 'white',
      color: '#6B7280',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500'
    },
    viewToggleButtonActive: {
      background: '#F3F4F6',
      color: '#111827'
    },
    errorBox: {
      backgroundColor: '#FEE2E2',
      border: '1px solid #F87171',
      color: '#B91C1C',
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }
  };

  const FolderView = viewMode === 'list' ? FolderList : FolderGrid;

  return (
    <div style={{ width: '100%' }}>
      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      {/* Navigation action bar */}
      <div style={styles.actionsBar}>
        <div style={styles.breadcrumbNav}>
          {currentFolder && (
            <button 
              type="button"
              onClick={handleNavigateUp}
              style={styles.backButton}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FaArrowLeft style={{ marginRight: '0.25rem' }} /> Back
            </button>
          )}
          
          <Breadcrumbs 
            breadcrumbs={breadcrumbs}
            onNavigateBreadcrumb={handleNavigateBreadcrumb}
            onNavigateHome={fetchRootFolders}
          />
        </div>
        {rightActions}
      </div>

      {loading && (
        <div style={styles.loadingContainer}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4423CC]"></div>
        </div>
      )}

      {/* Folders display section */}
      {!loading && (
        <>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {currentFolder ? `Contents of: ${currentFolder.name}` : 'All Folders'}
            </h2>
            <div style={styles.viewToggle}>
              {[
                { mode: 'grid', label: 'Grid', Icon: FaTh },
                { mode: 'list', label: 'List', Icon: FaList }
              ].map(({ mode, label, Icon }) => (
                <button
                  key={mode}
                  type="button"
                  title={`${label} view`}
                  aria-pressed={viewMode === mode}
                  onClick={() => changeViewMode(mode)}
                  style={{
                    ...styles.viewToggleButton,
                    ...(viewMode === mode ? styles.viewToggleButtonActive : {})
                  }}
                >
                  <Icon /> {label}
                </button>
              ))}
            </div>
          </div>
          {folders.length > 0 ? (
            <FolderView 
              folders={folders}
              showActions={showActions}
              activeActionMenu={activeActionMenu}
              onFolderClick={handleFolderClick}
              onToggleActionMenu={setActiveActionMenu}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
            />
          ) : (
            <div style={{ padding: '1rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
              No subfolders in this directory.
            </div>
          )}
        </>
      )}
    </div>
  );
};

FolderBrowser.propTypes = {
  initialFolderId: PropTypes.number,
  onFolderChange: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
  onEditFolder: PropTypes.func,
  onDeleteFolder: PropTypes.func,
  rightActions: PropTypes.node,
  filterClass: PropTypes.string,
  filterSubject: PropTypes.string,
  filterCourseTarget: PropTypes.string,
  refreshKey: PropTypes.number
};

export default FolderBrowser;
